import type { NoticeTemplateSample } from './NoticeTemplatePreview';

export type NoticePreview = { addresseeRole: string | null; blocks: NoticeTemplateSample['blocks']; known: Record<string, string> };
/** What each blank holds, for a blank still waiting on something the customer has not entered. */
const blankLabels: Record<string, string> = {
  addressee: 'who the notice is addressed to', copies: 'who gets a copy', services: 'the work on this job', property: 'the job’s property',
  orderedBy: 'who hired you', contractingParty: 'who hired you', firstFurnished: 'your first day on the job', lastFurnished: 'your last day on the job', signature: 'your signature',
  lienorName: 'your company’s name', lienorAddress: 'your company’s mailing address', lienorPhone: 'your company’s telephone', lienorEmail: 'your company’s email', date: 'today’s date', owner: 'the owner',
  contractAmount: 'your contract amount', amountUnpaid: 'the amount still unpaid', contractor: 'the contractor', lender: 'the construction lender',
  surety: 'the surety', ownerDesignee: 'the owner’s designee', additionalNoticeDesignee: 'the additional notice designee',
  feeSimpleTitleholder: 'the fee simple titleholder', publicAuthority: 'the public authority',
};

/**
 * The notice the customer is about to sign, as it will print: the statute's
 * wording with each blank filled from the job, the company and what the customer
 * has entered on this page. A blank still to be entered is marked, so nothing
 * on the signed notice is a surprise.
 */
export function CustomerNoticePreview({ title, preview, values, labels = {} }: { title: string; preview: NoticePreview; values: Record<string, string>;
  /** What a blank this list does not name holds, such as a fact the notice states (`{{fact:key}}`). */
  labels?: Record<string, string> }) {
  const filled = { ...preview.known, ...Object.fromEntries(Object.entries(values).filter(([, value]) => value.trim())) };
  const fill = (text: string) => text.split(/(\{\{(?:fact:)?\w+\}\})/).map((part, index) => {
    const field = part.match(/^\{\{((?:fact:)?\w+)\}\}$/)?.[1];
    if (!field) return part;
    return filled[field] ? <mark key={index} className="filled">{filled[field]}</mark>
      : <span key={index} className="blank">[{blankLabels[field] ?? labels[field] ?? field}]</span>;
  });
  return <div className="notice-paper" aria-label={`Preview of the ${title}`}>
    {preview.blocks.map((block, index) => block.kind === 'bullets'
      ? <ul key={index}>{block.items.map(item => <li key={item}>{fill(item)}</li>)}</ul>
      : block.kind === 'entry' ? <p key={index} className="notice-entry"><span>{block.label}</span> {fill(block.value)}</p>
        : block.kind === 'heading' ? <h4 key={index}>{fill(block.text)}</h4>
          : block.kind === 'warning' ? <p key={index} className="notice-warning">{fill(block.text)}</p>
            : <p key={index}>{block.kind === 'small' ? <small>{fill(block.text)}</small> : fill(block.text)}</p>)}
  </div>;
}
